import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotebookContext } from "@/src/pages/study/hooks/useNotebook";
import {
  AssistantIf,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAssistantApi,
} from "@assistant-ui/react";
import {
  ArchiveIcon,
  MessageCircleMore,
  PlusIcon,
  SquarePen,
  TrashIcon,
} from "lucide-react";
import type { FC } from "react";

export const ThreadList: FC = () => {
  return (
    <ThreadListPrimitive.Root className="aui-root aui-thread-list-root flex flex-col gap-1">
      <ThreadListNew />
      <AssistantIf condition={({ threads }) => threads.isLoading}>
        <ThreadListSkeleton />
      </AssistantIf>
      <AssistantIf condition={({ threads }) => !threads.isLoading}>
        <ThreadListPrimitive.Items components={{ ThreadListItem }} />
      </AssistantIf>
    </ThreadListPrimitive.Root>
  );
};

const ThreadListNew: FC = () => {
  const api = useAssistantApi();
  const { navigatePage } = useNotebookContext();
  return (
    <ThreadListPrimitive.New asChild>
      <Button
        variant="outline"
        className="aui-thread-list-new h-9 justify-start group-data-[collapsible=icon]:justify-center gap-2 rounded-lg px-3 text-sm hover:bg-theme-2 data-active:bg-theme-2 text-theme-0 group-data-[collapsible=icon]:mb-4 border-theme"
        onClick={() => {
          navigatePage({ type: "chat" });
          // name the past chat based on most recent message
          const messages = api.thread().getState().messages;
          if (!messages.length) return;
          if (!messages[messages.length - 1].parts.length) return;
          const lastPart = messages[messages.length - 1].parts[0];
          if (lastPart.type === "text")
            api.threadListItem().rename(lastPart.text.slice(0, 30));
          else if (lastPart.type === "tool-call")
            api.threadListItem().rename(`Created ${lastPart.toolName}`);
        }}
      >
        <SquarePen className="size-4" />
        <span className="group-data-[collapsible=icon]:hidden">New Chat</span>
      </Button>
    </ThreadListPrimitive.New>
  );
};

const ThreadListSkeleton: FC = () => {
  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          role="status"
          aria-label="Loading threads"
          className="aui-thread-list-skeleton-wrapper flex h-9 items-center px-3"
        >
          <Skeleton className="aui-thread-list-skeleton h-4 w-full" />
        </div>
      ))}
    </div>
  );
};

const ThreadListItem: FC = () => {
  const { navigatePage } = useNotebookContext();
  return (
    <ThreadListItemPrimitive.Root className="aui-thread-list-item group flex h-9 items-center rounded-lg transition-colors hover:bg-theme-2 focus-visible:bg-theme-2 focus-visible:outline-none data-active:bg-gray-400/20 text-theme-0">
      <ThreadListItemPrimitive.Trigger
        className="aui-thread-list-item-trigger flex h-full flex-1 items-center truncate px-3 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:flex-col text-start text-sm"
        onClick={() => navigatePage({ type: "chat" })}
      >
        <span className="group-data-[collapsible=icon]:hidden">
          <ThreadListItemPrimitive.Title fallback="New Chat" />
        </span>
        <span className="hidden group-data-[collapsible=icon]:block m-auto">
          <MessageCircleMore className="text-theme-0 size-5" />
        </span>
      </ThreadListItemPrimitive.Trigger>
      <ThreadListItemArchive />
    </ThreadListItemPrimitive.Root>
  );
};

const ThreadListItemArchive: FC = () => {
  return (
    <ThreadListItemPrimitive.Archive asChild>
      <TooltipIconButton
        variant="ghost"
        tooltip="Delete thread"
        className="aui-thread-list-item-archive mr-2 size-7 p-0 opacity-0 transition-opacity group-hover:opacity-100 group-data-[collapsible=icon]:hidden"
      >
        <TrashIcon className="size-4" />
      </TooltipIconButton>
    </ThreadListItemPrimitive.Archive>
  );
};
